<template>
  <v-container class="pt-6">
    <v-row>
      <v-col cols="12">
        <v-skeleton-loader
          :loading="requestLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-card class="elevation-12 mb-6">
            <v-card-title class="px-9 pt-7">
              <v-row no-gutters>
                <v-col
                  v-if="request.bulkOtherChange"
                  class="word-break-word"
                >
                  {{ request.id }} - Hromadná ostatní změna - {{ request.type.localizedText }}
                </v-col>
                <v-col
                  v-else-if="request.lvConnectionAgreementUpdate"
                  class="word-break-word"
                >
                  {{ request.id }} - Aktualizace Rámcových smluv NN
                  - {{ request.type.localizedText }}
                </v-col>
                <v-col
                  v-else
                  class="word-break-word"
                >
                  {{ request.id }} - {{ request.type.localizedText }}
                </v-col>
                <v-col
                  v-if="
                    !isRequestSent
                    && request.type.name === 'BULK_LV_AGREEMENT_UPDATE_PARENT'
                  "
                  cols="auto" class="ml-auto pl-3 mr-n3"
                >
                  <form @submit.prevent="submit">
                    <v-btn
                      depressed
                      type="submit"
                      height="40px"
                      min-width="125px"
                      color="primary"
                    >
                      Odeslat požadavek
                    </v-btn>
                  </form>
                </v-col>

                <v-col
                  v-if="!request.individual"
                  cols="auto" class="ml-auto pl-3"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="request.parentId"
                        :to="{
                          name: 'requests.detail',
                          params: {
                            id: request.parentId,
                          },
                        }"
                        v-on="on"
                        v-bind="attrs"
                        color="primary"
                        fab
                        dark
                        x-small
                        class="elevation-0"
                      >
                        <v-icon dark>mdi-arrow-collapse-up</v-icon>
                      </v-btn>
                    </template>

                    <span>
                      Přejít na nadřazený požadavek
                    </span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-card-title>

            <v-card-text class="px-9">
              <v-row class="justify-space-between">
                <template v-if="request.individual">
                  <v-col>
                    <div class="request-label">
                      Obchodní partner
                    </div>

                    <div class="request-value mt-2">
                      {{ request.partner }}
                    </div>
                  </v-col>

                  <v-col v-if="request.groupRequest !== true">
                    <div class="request-label">
                      EAN
                    </div>

                    <div class="request-value mt-2">
                      <router-link
                        :to="{
                          name: 'collectionPoint.detail',
                          params: {
                            ean: request.ean
                          }
                        }"
                      >
                      {{ request.ean }}
                      </router-link>
                    </div>
                  </v-col>
                </template>

                <template v-if="!request.individual && request.bulkOtherChange">
                  <v-col v-if="request.bulkOtherChange.organizationName">
                    <div class="request-label">
                      Obchodní partner
                    </div>

                    <div class="request-value mt-2">
                      {{ request.bulkOtherChange.organizationName }}
                    </div>
                  </v-col>

                  <v-col v-if="request.groupRequest !== true">
                    <div class="request-label">
                      EAN
                    </div>

                    <div class="request-value mt-2">
                      <router-link
                        :to="{
                          name: 'collectionPoint.detail',
                          params: {
                            ean: request.bulkOtherChange.ean
                          }
                        }"
                      >
                      {{ request.bulkOtherChange.ean }}
                      </router-link>
                    </div>
                  </v-col>
                </template>

                <template v-if="!request.individual && request.lvConnectionAgreementUpdate">
                  <v-col>
                    <div class="request-label">
                      Obchodní partner
                    </div>

                    <div class="request-value mt-2">
                      {{ request.lvConnectionAgreementUpdate.nameOrCompanyName }}
                    </div>
                  </v-col>

                  <v-col v-if="request.groupRequest !== true">
                    <div class="request-label">
                      EAN
                    </div>

                    <div class="request-value mt-2">
                      <router-link
                        :to="{
                          name: 'collectionPoint.detail',
                          params: {
                            ean: request.lvConnectionAgreementUpdate.ean
                          }
                        }"
                      >
                      {{ request.lvConnectionAgreementUpdate.ean }}
                      </router-link>
                    </div>
                  </v-col>
                </template>

                <v-col
                  cols="auto"
                  :class="{
                    'ml-auto': !request.individual,
                  }"
                >
                  <div class="request-label">
                    Status
                  </div>

                  <Status :status="request.status" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-skeleton-loader>

        <v-skeleton-loader
          v-if="
            request.type.name !== 'INDIVIDUAL_CAPACITY_CHANGE'
          "
          :loading="requestLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <InsertedValues v-model="request" />
        </v-skeleton-loader>

        <template v-else>
          <div
            v-for="(requestChildren, index) in request.children"
            :key="index"
          >
            <InsertedValues
              v-if="showInsertedValues(index)"
              v-model="request.children[index]"
            />
          </div>
        </template>

        <AgreementNNRequestForm
          v-if="request.lvConnectionAgreementUpdate"
          :loading="requestLoading"
          :request="request"
        />

        <v-skeleton-loader
          :loading="requestLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-card class="elevation-12 mb-6">
            <v-card-title class="px-9 pt-7">
              Aktivity požadavku
            </v-card-title>

            <v-card-text>
              <RequestActivities
                v-model="requestActivities"
                @onChangeOptions="loadRequestActivities"
              />
            </v-card-text>
          </v-card>
        </v-skeleton-loader>

        <v-skeleton-loader
          v-if="request.type.name === 'BULK_OTHER_CHANGE_PARENT'"
          :loading="requestLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-card class="elevation-12 mb-6">
            <v-card-title class="px-9 pt-7">
              Podřízené požadavky
            </v-card-title>

            <v-card-text>
              <TableSubRequests
                :value="childrenRequests"
                @openNoteDialog="openNoteDialog($event)"
                @onChangeOptions="loadRequestActivities"
              />
            </v-card-text>
          </v-card>
        </v-skeleton-loader>

        <v-skeleton-loader
          v-if="request.type.name === 'BULK_LV_AGREEMENT_UPDATE_PARENT'"
          :loading="requestLoading"
          :types="skeleton"
          type="article"
          class="mb-6"
          height="266"
        >
          <v-card class="elevation-12 mb-6">
            <v-card-title class="px-9 pt-7">
              Podřízené požadavky
            </v-card-title>

            <v-card-subtitle class="px-9 pt-2">
              K podřízeným požadavkům doplňte přílohy případně odečty
              a požadavek odešlete ke zpracování
            </v-card-subtitle>

            <v-card-text>
              <TableSubRequestsAgreementNN
                v-model="childrenRequests"
                :isParentRequestSent = isRequestSent
                @openNoteDialog="openNoteDialog($event)"
              />
            </v-card-text>
          </v-card>
        </v-skeleton-loader>
      </v-col>
    </v-row>
    <MessageDialog v-model="dialog" />
  </v-container>
</template>

<script>
import Status from '@/components/Status.vue';
import RequestActivities from '@/components/tables/RequestActivities.vue';
import TableSubRequests from '@/components/tables/TableSubRequests.vue';
import TableSubRequestsAgreementNN from '@/components/tables/TableSubRequestsAgreementNN.vue';
import AgreementNNRequestForm from '@/components/AgreementNNRequestForm.vue';
import InsertedValues from '@/components/InsertedValues.vue';
import MessageDialog from '@/components/dialogs/MessageDialog.vue';
import { mapActions } from 'vuex';

export default {
  name: 'RequestDetail',
  components: {
    Status,
    RequestActivities,
    InsertedValues,
    TableSubRequests,
    TableSubRequestsAgreementNN,
    AgreementNNRequestForm,
    MessageDialog,
  },
  data() {
    return {
      skeleton: {
        article: 'heading, paragraph@2',
      },
      dialog: {
        content: '',
        show: false,
      },
      childrenRequests: [],
      request: {
        id: this.$route.params.id,
        availableTransitions: [],
        type: {},
        status: {},
      },
      requestActivities: {
        items: [],
        options: {
          page: 1,
          itemsPerPage: this.$store.state.settings.tables.requestsActivities.itemsPerPage,
        },
        serverItemsLength: 0,
        loading: false,
      },
      cancel: null,
      requestLoading: false,
      isRequestSent: false,
    };
  },
  computed: {
    monthlyInChildren() {
      let returnBool = false;
      Object.values(this.request.children).find((child) => {
        if (child.type.name === 'INDIVIDUAL_CAPACITY_MONTHLY_CHANGE') {
          returnBool = true;
        }
        return returnBool;
      });
      return returnBool;
    },
  },
  methods: {
    ...mapActions({
      getRequest: 'requests/getRequest',
      getRequestActivities: 'requests/getRequestActivities',
      sendLVAgreementToSAP: 'requests/sendLVAgreementToSAP',
    }),
    openNoteDialog(data) {
      this.dialog.show = true;
      this.dialog.title = `Komentář k požadavku č. ${data.id}`;
      this.dialog.data = data;
      this.dialog.content = data.lastActivityNote;
    },
    loadRequest() {
      this.requestLoading = true;
      this.getRequest(this.$route.params.id)
        .then((response) => {
          this.request = response.data;
          this.childrenRequests = response.data.children;
          if (response.data.status.name !== 'NEW') {
            this.isRequestSent = true;
          }
        }).catch((error) => {
          if (error.response && error.response.status === 404) {
            this.$router.push({
              path: '/404',
            });
          }
        }).finally(() => {
          this.requestLoading = false;
        });
    },
    loadRequestActivities() {
      this.requestActivities.loading = true;
      const searchParams = {
        id: this.request.id,
        options: this.requestActivities.options,
      };
      if (this.cancel) {
        this.cancel();
        this.cancel = null;
      }
      searchParams.cancelHandler = (cancel) => {
        this.cancel = cancel;
      };
      this.getRequestActivities(searchParams)
        .then((response) => {
          this.requestActivities.items = response.data.items;
          this.requestActivities.serverItemsLength = response.data.totalElements;
        })
        .finally(() => {
          this.requestActivities.loading = false;
        });
    },
    showInsertedValues(index) {
      if (this.monthlyInChildren && this.request.children[index].type.name === 'INDIVIDUAL_CAPACITY_ANNUAL_CHANGE') {
        return false;
      }
      return true;
    },
    submit() {
      const paramSAP = {
        id: this.$route.params.id,
      };
      this.sendLVAgreementToSAP(paramSAP)
        .then((response) => {
          if (response.data.status.name !== 'NEW') {
            this.isRequestSent = true;
            this.loadRequest();
          }
        }).catch((error) => {
          if (error.response && error.response.status === 404) {
            this.$router.push({
              path: '/404',
            });
          }
        }).finally(() => {
          this.requestLoading = false;
        });
    },
  },
  watch: {
    '$route.params.id': {
      handler() {
        this.loadRequest();
      },
    },
  },
  created() {
    this.loadRequest();
  },
};
</script>
